import { useApiClient } from "@/infrastructure/rest";
import { downloadBlob } from "@/infrastructure/rest/download-blob-utils.ts";
import { EvaluatieDetailsDto, PutEvaluatieDetailsDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { DOSSIERS_QUERY_KEY } from "../dossier";
import { EVALUATIE_QUERY_KEY } from "./constants";
import { Evaluatie } from "./domain";

export function useGetDossierEvaluatieById(dossierId: string, options?: Omit<UseQueryOptions<Evaluatie | null>, "queryFn" | "queryKey">) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, EVALUATIE_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<EvaluatieDetailsDto>(`/dossiers/${dossierId}/evaluatie`);
            return data ? Evaluatie.fromDto(data) : null;
        },
        ...options,
    });
}

export type PutEvaluatie = PutEvaluatieDetailsDto & { dossierId: string };

export function usePutEvaluatie() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        onMutate: async ({ dossierId, ...data }) => {
            // don't invalidate queries here, because this PUT is triggered onChange. Instead, optimistically update the cache
            const queryKey = [DOSSIERS_QUERY_KEY, dossierId, EVALUATIE_QUERY_KEY] as const;
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<Evaluatie | null>(queryKey) ?? null;
            queryClient.setQueryData<Evaluatie | null>(queryKey, data);
            return { queryKey, previous };
        },
        mutationFn: async ({ dossierId, ...data }: PutEvaluatie) => {
            await apiClient.put(`/dossiers/${dossierId}/evaluatie`, data);
        },
        onError: (error, _, context) => {
            if (context) {
                queryClient.setQueryData(context.queryKey, context.previous);
            }
            console.error("Error putting evaluatie", error);
        },
    });
}

type ExportEvaluatie = { dossierId: string };

export function useExportEvaluatie() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId }: ExportEvaluatie) => {
            const download = await apiClient.downloadFile(`/dossiers/${dossierId}/evaluatie/khr`);
            if (download) {
                downloadBlob(download.blob, download.fileName);
            }
        },
        onError: (error) => {
            console.error("Error downloading evaluatie pdf", error);
        },
    });
}
